/**
 * Performance › Real users (`/performance/real`) — pixel RUM: the
 * Core Web Vitals your actual visitors experienced (form-factor /
 * country / connection controls, five CWV tiles, metric-selector
 * trend chart) behind a `RumPlaceholderBanner` when nothing's
 * flowing yet.
 *
 * Promoted from the old `/performance?view=real` tab to its own
 * route (2026-07-26) so the sidebar section panel can list it; that
 * legacy URL still redirects here via `/performance`'s `beforeLoad`.
 */
import { CompareSitePicker, useCompareSites } from '@/components/primitives/CompareSitePicker';
import { DEFAULT_PAGE_RANGE, type PageRange } from '@/components/primitives/PageRangePill';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import {
  CWV_METRIC_KEYS,
  type MetricKey,
  NETWORK_TYPES_BY_CONNECTION,
  type PerfConnection,
  type PerfFormFactor,
  PerformanceSection,
} from '@/components/rum/PerformanceSection';
import { RumPlaceholderBanner } from '@/components/rum/RumPlaceholders';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { DEMO_EMBED } from '@/lib/demo';
import {
  useSitePixelBreakdown,
  useSitePixelHealth,
  useSitePixelPerf,
  useSitePixelPerfByCountry,
  useSitePixelPerfByPage,
  useSitePixelPerfTrend,
} from '@/lib/queries/pixel';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

interface RealSearch {
  /** Selected vital. In the URL so a metric view is shareable and the
   *  Overview's summary tiles can deep-link into one. */
  metric?: MetricKey;
  /** Guest site uuid to compare against. Shareable, like `metric`. */
  compare?: string;
}

const isMetricKey = (value: unknown): value is MetricKey =>
  CWV_METRIC_KEYS.includes(value as MetricKey);

export const Route = createFileRoute('/_app/performance/real')({
  validateSearch: (search: Record<string, unknown>): RealSearch => ({
    ...(isMetricKey(search.metric) ? { metric: search.metric } : {}),
    ...(typeof search.compare === 'string' && search.compare !== ''
      ? { compare: search.compare }
      : {}),
  }),
  component: RealUsersPage,
});

function RealUsersPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Real users" parent={{ label: 'Performance', href: '/performance' }}>
      {siteUuid && <RealUsersBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function RealUsersBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const { metric, compare } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [range, setRange] = useState<PageRange>(DEFAULT_PAGE_RANGE);
  const [formFactor, setFormFactor] = useState<PerfFormFactor>('all');
  const [country, setCountry] = useState<string>('all');
  const [connection, setConnection] = useState<PerfConnection>('all');

  // Compare-site resolution — no self option: the RUM overlay only
  // makes sense across two different sites.
  const {
    options: compareOptions,
    compareUuid,
    compareName,
    homeName,
  } = useCompareSites(siteUuid, compare);

  // Re-checked at the point of use (not just `??`-defaulted): see the
  // validateSearch note on `/performance` — a parent route's untouched
  // copy of a bogus `?metric=` can arrive typed as a valid key.
  const activeMetric: MetricKey = isMetricKey(metric) ? metric : 'lcp';
  // `replace` — flipping through five pills is exploration, not
  // navigation; stacking a history entry per pill would make Back mean
  // "previous metric" for a dozen presses before it left the page.
  const selectMetric = (next: MetricKey): void => {
    void navigate({ search: (prev) => ({ ...prev, metric: next }), replace: true });
  };

  // Pixel install signal — drives the install gate (the "install the
  // pixel" banner + awaiting-first-beacon copy).
  const health = useSitePixelHealth(siteUuid);
  const beaconsToday = health.data?.beacons_today ?? 0;
  const lastBeaconAt = health.data?.last_beacon_at;
  const everSeenBeacon = !!lastBeaconAt && !lastBeaconAt.startsWith('1970');

  // Map the three filter selectors into the wire-level params the
  // pixel hooks accept. `undefined` for "all" so the no-filter case
  // routes to the fast MV-backed pipe (the Tinybird client decides);
  // a populated array tips perf + perf_trend onto the raw-events
  // filtered pipe.
  const formFactorParam = formFactor === 'all' ? undefined : [formFactor];
  const countryParam = country === 'all' ? undefined : [country];
  const networkParam = connection === 'all' ? undefined : NETWORK_TYPES_BY_CONNECTION[connection];

  // Two pipes drive the surface. We deliberately do NOT use the
  // /pixel/bundle endpoint here: bundle is the cold-load fast path
  // for the Traffic surface. This route only needs perf + perf_trend
  // + health, and the filters are part of the request — per-pipe
  // hooks keyed by the filter values cache correctly for free.
  const perf = useSitePixelPerf(siteUuid, {
    days: range,
    formFactors: formFactorParam,
    countryCodes: countryParam,
    networkTypes: networkParam,
  });
  // Trend always fetches every form factor so the per-form-factor
  // legend in the trend chart works regardless of the tile-row
  // filter. Server-side payload is tiny (≤90 rows for a 30d window).
  // Country + network filters DO thread through — they're audience
  // narrowing, not display knobs.
  const trend = useSitePixelPerfTrend(siteUuid, {
    days: range,
    countryCodes: countryParam,
    networkTypes: networkParam,
  });
  // Same series over twice the window so the trend chart can overlay the
  // PREVIOUS period as a dashed comparison line (the N days before the
  // current N). Same filters; the chart splits off the older half.
  const trendCompare = useSitePixelPerfTrend(siteUuid, {
    days: range * 2,
    countryCodes: countryParam,
    networkTypes: networkParam,
  });
  // Guest-site series when `?compare=` is active — the same two pipes,
  // same filters, keyed by the guest uuid (the hooks no-op on null).
  // Tenancy is enforced server-side per site, so no extra client guard
  // beyond the origins-list validation in `useCompareSites`.
  const guestPerf = useSitePixelPerf(compareUuid, {
    days: range,
    formFactors: formFactorParam,
    countryCodes: countryParam,
    networkTypes: networkParam,
  });
  const guestTrend = useSitePixelPerfTrend(compareUuid, {
    days: range,
    countryCodes: countryParam,
    networkTypes: networkParam,
  });
  // Country ranking — fetched even when a country filter is active
  // (the user may want to compare against the rest of the world). We
  // do thread formFactor + connection though, so the ranking reflects
  // the active audience.
  const perfByCountry = useSitePixelPerfByCountry(siteUuid, {
    days: range,
    formFactors: formFactorParam,
    networkTypes: networkParam,
    limit: 30,
  });
  // Per-page ranking — drives the Page (worst) + Popularity cards. Same
  // audience filters as the country ranking so all three agree.
  const perfByPage = useSitePixelPerfByPage(siteUuid, {
    days: range,
    formFactors: formFactorParam,
    networkTypes: networkParam,
    limit: 30,
  });
  // Country dropdown options — only countries that actually have
  // beacons for this site, sorted by traffic so the country the user
  // most likely wants is at the top of the dropdown.
  const countries = useSitePixelBreakdown(siteUuid, 'country_code', {
    days: range,
    limit: 50,
    sort: 'pageviews',
  });
  const countryOptions = useMemo<string[]>(
    () =>
      (countries.data ?? [])
        .map((r) => r.bucket)
        .filter((code): code is string => typeof code === 'string' && code.length > 0),
    [countries.data],
  );

  // Install-state gate: mirror the Traffic page so the two surfaces
  // agree on what "installed" means. Either a beacon today OR any
  // historical sample in the perf payload is enough.
  const anyPerfSamples = (perf.data ?? []).some((r) =>
    [
      r.lcp_sample_count,
      r.inp_sample_count,
      r.cls_sample_count,
      r.ttfb_sample_count,
      r.fcp_sample_count,
    ].some((n) => n > 0),
  );
  const installed = beaconsToday > 0 || anyPerfSamples;
  const confirmedEmpty = health.isSuccess && perf.isSuccess && !installed;
  const awaitingFirstBeacon = confirmedEmpty && everSeenBeacon;

  return (
    <div className="space-y-6">
      <SectionBoundary label="this view">
        <RumPlaceholderBanner
          scope="site"
          siteUuid={siteUuid}
          hidden={!confirmedEmpty}
          awaitingFirstBeacon={awaitingFirstBeacon}
        />
        <PerformanceSection
          perf={perf.data}
          trend={trend.data}
          trendCompare={trendCompare.data}
          perfByCountry={perfByCountry.data}
          perfByPage={perfByPage.data}
          // `isLoading` (not `isFetching`) is the initial-load gate:
          // it's only true while the very first fetch is in flight
          // with no cache. Filter refetches use `keepPreviousData`,
          // so this stays false during those — no data → loading-
          // shell flicker on filter switch.
          loading={perf.isLoading || trend.isLoading}
          range={range}
          onRangeChange={setRange}
          formFactor={formFactor}
          onFormFactorChange={setFormFactor}
          country={country}
          onCountryChange={setCountry}
          countryOptions={countryOptions}
          connection={connection}
          onConnectionChange={setConnection}
          metric={activeMetric}
          onMetricChange={selectMetric}
          siteUuid={siteUuid}
          comparePerf={compareUuid ? guestPerf.data : undefined}
          compareTrend={compareUuid ? guestTrend.data : undefined}
          compareName={compareUuid ? (compareName ?? 'Other site') : undefined}
          homeName={homeName}
          // Compare picker is route-level chrome — embed slices don't
          // want it (same treatment the old tab bar got), and it renders
          // null on single-site orgs. It rides in the section's filter
          // row so the page has one control strip, not two.
          actions={
            DEMO_EMBED ? null : (
              <CompareSitePicker
                value={compareUuid}
                options={compareOptions}
                onChange={(next) =>
                  void navigate({
                    search: (prev) => ({
                      ...(prev.metric ? { metric: prev.metric } : {}),
                      ...(next ? { compare: next } : {}),
                    }),
                  })
                }
              />
            )
          }
        />
      </SectionBoundary>
    </div>
  );
}
